import type { Plant, PlantSeedStarting } from '@/data/plants';

type PlantingPhase = 'indoorStart' | 'transplant' | 'directSow';
export type SeedStartingMode = 'seed-started' | 'direct-sow' | 'transplant-only';

export interface CalculatedPlantingWindow {
  sourceText: string;
  label: string | null;
  startDate: Date | null;
  endDate: Date | null;
  notes: string[];
  status: 'dated' | 'note' | 'needs-zone';
}

export interface SeedStartingCalculation {
  zone: number | null;
  mode: SeedStartingMode;
  modeLabel: string;
  modeDescription: string;
  lastFrostDate: Date | null;
  lastFrostLabel: string | null;
  indoorStart?: CalculatedPlantingWindow;
  transplant?: CalculatedPlantingWindow;
  directSow?: CalculatedPlantingWindow;
  propagationNote?: string;
  transplantGuidance?: TransplantNurseryGuidance;
  seedStarting?: PlantSeedStarting;
}

export interface TransplantNurseryGuidance {
  springWindow: {
    label: string;
    detail: string;
  };
  autumnWindow: {
    label: string;
    detail: string;
  };
  plantingMethod: string;
  criticalTimingNote: string;
}

export const TYPICAL_LAST_FROST_BY_ZONE: Record<number, { month: number; day: number }> = {
  3: { month: 5, day: 20 },
  4: { month: 5, day: 10 },
  5: { month: 4, day: 30 },
  6: { month: 4, day: 15 },
  7: { month: 4, day: 1 },
  8: { month: 3, day: 15 },
  9: { month: 2, day: 15 },
  10: { month: 1, day: 31 },
};

const PHASES: PlantingPhase[] = ['indoorStart', 'transplant', 'directSow'];

function dateAtNoon(year: number, month: number, day: number): Date {
  return new Date(year, month - 1, day, 12, 0, 0, 0);
}

export function getTypicalLastFrostDate(
  zone: number | null | undefined,
  year = new Date().getFullYear(),
): Date | null {
  if (!zone) return null;
  const frost = TYPICAL_LAST_FROST_BY_ZONE[zone];
  return frost ? dateAtNoon(year, frost.month, frost.day) : null;
}

export function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

export function addWeeks(date: Date, weeks: number): Date {
  return addDays(date, weeks * 7);
}

export function formatDateForUI(date: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

function formatShortDate(date: Date): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  }).format(date);
}

export function formatDateRangeForUI(startDate: Date, endDate: Date): string {
  if (startDate.getTime() === endDate.getTime()) {
    return formatDateForUI(startDate);
  }

  const sameYear = startDate.getFullYear() === endDate.getFullYear();
  const sameMonth = sameYear && startDate.getMonth() === endDate.getMonth();

  if (sameMonth) {
    return `${formatShortDate(startDate)} - ${endDate.getDate()}, ${endDate.getFullYear()}`;
  }

  if (sameYear) {
    return `${formatShortDate(startDate)} - ${formatDateForUI(endDate)}`;
  }

  return `${formatDateForUI(startDate)} - ${formatDateForUI(endDate)}`;
}

function cleanPlantingText(value: string | undefined): string | null {
  const text = value?.trim();
  if (!text || /^not necessary$/i.test(text)) return null;
  return text;
}

function extractPlantingNotes(text: string): string[] {
  const notes: string[] = [];

  if (/soil\s+is\s+workable|as soon as soil is workable/i.test(text)) {
    notes.push('Wait until soil is workable.');
  }

  const soilTempMatch = text.match(/soil\s*(?:is\s*)?(\d+)\s*°?\s*f\+?/i);
  if (soilTempMatch?.[1]) {
    notes.push(`Wait for soil ${soilTempMatch[1]}°F+.`);
  }

  if (/surface sow/i.test(text)) {
    notes.push('Surface sow.');
  }

  if (/seeds need light|needs light to germinate/i.test(text)) {
    notes.push('Seeds need light to germinate.');
  }

  if (/soak seeds|soak .* overnight/i.test(text)) {
    notes.push('Soak seeds before sowing if the planting note recommends it.');
  }

  return Array.from(new Set(notes));
}

function noteOnly(
  sourceText: string,
  note: string,
  notes: string[] = [],
  status: CalculatedPlantingWindow['status'] = 'note',
  label: string | null = null,
): CalculatedPlantingWindow {
  return {
    sourceText,
    label,
    startDate: null,
    endDate: null,
    notes: [note, ...notes],
    status,
  };
}

function buildWindow(
  sourceText: string,
  startDate: Date,
  endDate: Date,
  notes: string[],
  prefix?: string,
): CalculatedPlantingWindow {
  const label = prefix
    ? `${prefix} ${formatDateRangeForUI(startDate, endDate)}`
    : formatDateRangeForUI(startDate, endDate);

  return {
    sourceText,
    label,
    startDate,
    endDate,
    notes,
    status: 'dated',
  };
}

function seasonalWindowLabel(text: string): string | null {
  const normalized = text.toLowerCase();

  if (/late autumn to early spring/.test(normalized)) return 'Late autumn to early spring';
  if (/late winter to early spring/.test(normalized)) return 'Late winter to early spring';
  if (/late autumn through early spring/.test(normalized)) return 'Late autumn through early spring';
  if (/early spring or autumn|autumn or early spring/.test(normalized)) return 'Early spring or autumn';
  if (/spring[- ]autumn|spring to autumn/.test(normalized)) return 'Spring to autumn';
  if (/spring or autumn|spring and autumn/.test(normalized)) return 'Spring or autumn';
  if (/early spring/.test(normalized)) return 'Early spring';
  if (/late winter/.test(normalized)) return 'Late winter';
  if (/after last frost/.test(normalized)) return null;
  if (/\bspring\b/.test(normalized)) return 'Spring';
  if (/\bautumn\b|\bfall\b/.test(normalized)) return 'Autumn';

  return null;
}

function propagationNoteFor(text: string): string | null {
  const normalized = text.toLowerCase();

  if (/bare-root .*trees|bare root .*trees|bare-root trees|bare root trees/.test(normalized)) {
    return 'Usually planted as a bare-root nursery tree while dormant, not started from seed.';
  }
  if (/container-grown trees|container grown trees/.test(normalized)) {
    return 'Usually planted as a nursery tree; container-grown trees have a wider planting window in mild conditions.';
  }
  if (/bare-root .*canes|bare root .*canes|bare-root canes|bare root canes/.test(normalized)) {
    return 'Usually planted as bare-root canes while dormant, not started from seed.';
  }
  if (/\bcrowns?\b/.test(normalized)) {
    return 'Usually planted from crowns rather than started from seed.';
  }
  if (/\bdivisions?\b/.test(normalized)) {
    return 'Usually planted from divisions rather than started from seed.';
  }
  if (/purchased transplants|nursery stock|nursery plants/.test(normalized)) {
    return 'Usually planted from nursery-grown transplants rather than started from seed.';
  }
  if (/\bslips?\b/.test(normalized)) {
    return 'Usually planted from slips rather than started from seed.';
  }

  return null;
}

function springWindowFor(
  text: string,
  lastFrostDate: Date | null,
): TransplantNurseryGuidance['springWindow'] {
  const normalized = text.toLowerCase();
  const zoneAnchored = (startWeeksBefore: number, endWeeksAfter: number, fallback: string, detail: string) => {
    if (!lastFrostDate) return { label: fallback, detail };
    return {
      label: formatDateRangeForUI(addWeeks(lastFrostDate, -startWeeksBefore), addWeeks(lastFrostDate, endWeeksAfter)),
      detail,
    };
  };

  if (/late autumn to early spring|late autumn through early spring|late winter to early spring|late winter while dormant/.test(normalized)) {
    return zoneAnchored(
      6,
      -1,
      'Late winter to early spring',
      'Plant while dormant, before buds break and before active top growth begins.',
    );
  }

  if (/early spring|as soon as soil is workable/.test(normalized)) {
    return zoneAnchored(
      4,
      0,
      'Early spring',
      'Plant as soon as the soil is workable so roots establish before heat arrives.',
    );
  }

  if (/after last frost/.test(normalized)) {
    if (lastFrostDate) {
      return {
        label: `After ${formatDateForUI(lastFrostDate)}`,
        detail: 'Plant once frost risk has passed and spring conditions are settled.',
      };
    }
    return {
      label: 'After your last frost',
      detail: 'Plant once frost risk has passed and spring conditions are settled.',
    };
  }

  if (/\bspring\b/.test(normalized)) {
    return zoneAnchored(
      2,
      2,
      'Spring',
      'Plant early enough for roots to settle before summer heat.',
    );
  }

  return zoneAnchored(
    2,
    2,
    'Spring',
    'Use spring planting when soil can be worked and the plant can establish before heat.',
  );
}

function autumnWindowFor(text: string): TransplantNurseryGuidance['autumnWindow'] {
  const normalized = text.toLowerCase();

  if (/late autumn to early spring|late autumn through early spring/.test(normalized)) {
    return {
      label: 'Late autumn after leaf drop',
      detail: 'Plant while dormant, after leaves have dropped and before the ground freezes.',
    };
  }

  if (/spring-autumn|spring to autumn|early spring or autumn|spring or autumn|or autumn|autumn in mild climates/.test(normalized)) {
    return {
      label: 'Early autumn',
      detail: 'Plant early enough for roots to grow before winter; avoid late planting into cold, wet soil.',
    };
  }

  if (/container-grown trees can be planted year-round|any time in mild climates|year-round/.test(normalized)) {
    return {
      label: 'Autumn in mild climates',
      detail: 'Use autumn only where winters are mild and roots can keep growing after planting.',
    };
  }

  return {
    label: 'Usually skip autumn planting',
    detail: 'Use spring unless you have locally grown nursery stock and enough mild weather for roots to establish.',
  };
}

function plantingMethodFor(
  plant: Pick<Plant, 'name' | 'type'>,
  text: string,
  fallbackNote: string | null,
): string {
  const normalized = text.toLowerCase();

  if (/bare-root .*trees|bare root .*trees|bare-root trees|bare root trees/.test(normalized)) {
    return 'Plant a grafted bare-root nursery tree. Seed-grown fruit trees are not true-to-type, so nursery stock is the reliable path to known fruit quality.';
  }
  if (/container-grown trees|container grown trees/.test(normalized)) {
    return 'Plant a nursery-grown tree. Seed-grown fruit trees are slow and variable, so container or grafted stock is the practical choice.';
  }
  if (/bare-root .*canes|bare root .*canes|bare-root canes|bare root canes/.test(normalized)) {
    return 'Plant dormant bare-root canes. Named varieties are propagated vegetatively so the fruiting plant stays true to type.';
  }
  if (/\bcrowns?\b/.test(normalized)) {
    return 'Plant healthy crowns. Seed is possible for some crops, but crowns establish faster and reach useful harvest size sooner.';
  }
  if (/\btubers?\b/.test(normalized)) {
    return 'Plant firm tubers with visible growing points. Tubers are the standard planting form, not seed.';
  }
  if (/\bdivisions?\b/.test(normalized)) {
    return 'Plant divisions from a healthy parent plant. Divisions preserve the established plant’s traits better than seed.';
  }
  if (/\bslips?\b/.test(normalized)) {
    return 'Plant rooted slips. Slips are the standard planting form and establish faster than seed-grown plants.';
  }
  if (/purchased transplants|nursery stock|nursery plants/.test(normalized)) {
    return 'Plant nursery-grown transplants. They establish faster and more reliably than starting this plant from seed.';
  }
  if (/direct sow or allow to naturalize/.test(normalized)) {
    return 'Establish by direct sowing, transplanting small starts, or allowing existing plants to naturalize.';
  }
  if (plant.name === 'Strawberry') {
    return 'Plant crowns, runners, or nursery-grown plugs. Seed-grown strawberries are slow and variable, so starts are the practical choice.';
  }
  if (plant.name === 'Blueberry') {
    return 'Plant nursery-grown blueberry shrubs. Seed-grown blueberries are slow and variable, and named cultivars are propagated from selected stock.';
  }
  if (plant.type === 'fruit') {
    return `Plant nursery-grown ${plant.name.toLowerCase()} stock or rooted cuttings. Seed-grown plants are slow, variable, and usually not the best way to establish a productive planting.`;
  }

  return fallbackNote ?? 'Use nursery-grown planting stock rather than treating this as a standard seed-starting crop.';
}

function criticalTimingNoteFor(plant: Pick<Plant, 'name'>, text: string): string {
  const normalized = text.toLowerCase();

  if (plant.name === 'Asparagus') {
    return 'Do not harvest the first year; let crowns build a strong root system before taking spears.';
  }
  if (plant.name === 'Strawberry') {
    return 'Keep the crown level with the soil surface; buried crowns rot and exposed roots dry out.';
  }
  if (plant.name === 'Blueberry') {
    return 'Plant early enough for shallow roots to establish before heat, and keep the root zone consistently moist.';
  }
  if (/bare-root|bare root|dormant|bud break/.test(normalized)) {
    return 'Plant while dormant and before bud break so roots establish before leaves demand water.';
  }
  if (/\bcrowns?\b/.test(normalized)) {
    return 'Set crowns at the correct depth and keep the bed evenly moist while new roots establish.';
  }
  if (/\btubers?\b/.test(normalized)) {
    return 'Wait until frost risk has passed and soil is warming; cold, wet soil can rot tubers.';
  }
  if (/\bdivisions?\b/.test(normalized)) {
    return 'Keep divisions watered through establishment and protect them from harsh sun until new growth resumes.';
  }
  if (/after last frost|warm climates|soil/.test(normalized)) {
    return 'Plant after cold risk has passed so roots can establish without chilling or stalling.';
  }

  return 'Plant early enough for roots to establish before weather stress arrives.';
}

function buildTransplantGuidance(
  plant: Pick<Plant, 'name' | 'type'>,
  transplantText: string,
  lastFrostDate: Date | null,
): TransplantNurseryGuidance {
  const fallbackNote = propagationNoteFor(transplantText);
  return {
    springWindow: springWindowFor(transplantText, lastFrostDate),
    autumnWindow: autumnWindowFor(transplantText),
    plantingMethod: plantingMethodFor(plant, transplantText, fallbackNote),
    criticalTimingNote: criticalTimingNoteFor(plant, transplantText),
  };
}

function hasFrostReference(text: string): boolean {
  return /last frost|first autumn frost|ground freezes/i.test(text);
}

function parseFrostBasedPlantingWindow(
  text: string,
  lastFrostDate: Date | null,
): CalculatedPlantingWindow {
  const sourceText = text.trim();
  const notes = extractPlantingNotes(sourceText);
  const normalized = sourceText.replace(/[–—]/g, '-');
  const lower = normalized.toLowerCase();
  const seasonalLabel = seasonalWindowLabel(normalized);

  if (/first autumn frost|ground freezes|fall-planted|autumn/i.test(normalized) && !/last frost/i.test(normalized)) {
    return noteOnly(
      sourceText,
      'This uses autumn or first-frost timing, so keep the planting note as written.',
      notes,
      'note',
      seasonalLabel,
    );
  }

  if (!lastFrostDate) {
    if (seasonalLabel && !hasFrostReference(normalized)) {
      return noteOnly(sourceText, 'Use the seasonal timing note for this plant.', notes, 'note', seasonalLabel);
    }
    return noteOnly(sourceText, 'Set your growing zone to calculate personalized dates.', [], 'needs-zone');
  }

  const beforeMatch = normalized.match(/(\d+)(?:\s*-\s*(\d+))?\s+weeks?\s+before\s+last\s+frost/i);
  if (beforeMatch?.[1]) {
    const firstWeeks = Number(beforeMatch[1]);
    const secondWeeks = beforeMatch[2] ? Number(beforeMatch[2]) : firstWeeks;
    const earlyWeeks = Math.max(firstWeeks, secondWeeks);
    const lateWeeks = Math.min(firstWeeks, secondWeeks);
    return buildWindow(
      sourceText,
      addWeeks(lastFrostDate, -earlyWeeks),
      addWeeks(lastFrostDate, -lateWeeks),
      notes,
    );
  }

  const afterMatch = normalized.match(/(\d+)(?:\s*-\s*(\d+))?\s+weeks?\s+after\s+last\s+frost/i);
  if (afterMatch?.[1]) {
    const firstWeeks = Number(afterMatch[1]);
    const secondWeeks = afterMatch[2] ? Number(afterMatch[2]) : firstWeeks;
    const earlyWeeks = Math.min(firstWeeks, secondWeeks);
    const lateWeeks = Math.max(firstWeeks, secondWeeks);
    return buildWindow(
      sourceText,
      addWeeks(lastFrostDate, earlyWeeks),
      addWeeks(lastFrostDate, lateWeeks),
      notes,
    );
  }

  if (/\bafter\s+last\s+frost\b/i.test(normalized)) {
    return buildWindow(sourceText, lastFrostDate, lastFrostDate, notes, 'After');
  }

  if (/\bbefore\s+last\s+frost\b/i.test(normalized) || /\blast\s+frost\b/i.test(normalized)) {
    return noteOnly(sourceText, 'This references last frost but is too vague to convert safely.', notes);
  }

  if (lower.includes('early spring') || lower.includes('spring')) {
    return noteOnly(
      sourceText,
      'This uses seasonal timing, so keep the planting note as written.',
      notes,
      'note',
      seasonalLabel,
    );
  }

  return noteOnly(sourceText, 'This planting note is not specific enough for an exact frost-based date.', notes);
}

function getCalculationMode(plant: Pick<Plant, 'planting' | 'seedStarting' | 'type' | 'tempRequirements'>): SeedStartingMode {
  const hasIndoorStart = Boolean(cleanPlantingText(plant.planting.indoorStart));
  const hasDirectSow = Boolean(cleanPlantingText(plant.planting.directSow));
  const hasTransplant = Boolean(cleanPlantingText(plant.planting.transplant));
  const perennialLike = plant.tempRequirements?.season === 'perennial';

  if (perennialLike && !hasIndoorStart && (!hasDirectSow || hasTransplant)) return 'transplant-only';
  if (plant.seedStarting?.bestMethod === 'direct-sow') return 'direct-sow';
  if (plant.seedStarting?.bestMethod === 'transplant-only') return 'transplant-only';
  if (plant.seedStarting?.bestMethod === 'start-indoors' || hasIndoorStart) return 'seed-started';
  if (hasDirectSow && !hasTransplant) return 'direct-sow';
  return 'transplant-only';
}

function modeCopy(mode: SeedStartingMode): Pick<SeedStartingCalculation, 'modeLabel' | 'modeDescription'> {
  if (mode === 'seed-started') {
    return {
      modeLabel: 'Seed-started crop',
      modeDescription: 'Use indoor-start, transplant, and direct-sow timing where this plant supports them.',
    };
  }
  if (mode === 'direct-sow') {
    return {
      modeLabel: 'Direct-sow crop',
      modeDescription: 'This plant is usually sown directly outdoors rather than started indoors.',
    };
  }
  return {
    modeLabel: 'Transplant or nursery-stock plant',
    modeDescription: 'This plant is typically established from nursery stock, crowns, or cuttings rather than started from seed.',
  };
}

export function calculateSeedStartingDates(
  plant: Pick<Plant, 'name' | 'planting' | 'seedStarting' | 'type' | 'tempRequirements'>,
  zone: number | null | undefined,
  year = new Date().getFullYear(),
): SeedStartingCalculation {
  const lastFrostDate = getTypicalLastFrostDate(zone, year);
  const mode = getCalculationMode(plant);
  const transplantText = cleanPlantingText(plant.planting.transplant);
  const propagationNote = transplantText ? propagationNoteFor(transplantText) : null;
  const calculation: SeedStartingCalculation = {
    zone: zone ?? null,
    mode,
    ...modeCopy(mode),
    lastFrostDate,
    lastFrostLabel: lastFrostDate ? formatDateForUI(lastFrostDate) : null,
    ...(propagationNote ? { propagationNote } : {}),
    ...(mode === 'transplant-only' && transplantText
      ? { transplantGuidance: buildTransplantGuidance(plant, transplantText, lastFrostDate) }
      : {}),
    ...(plant.seedStarting ? { seedStarting: plant.seedStarting } : {}),
  };

  for (const phase of PHASES) {
    const text = cleanPlantingText(plant.planting[phase]);
    if (!text) continue;
    calculation[phase] = parseFrostBasedPlantingWindow(text, lastFrostDate);
  }

  return calculation;
}
